/**
 * <park-admin-empty> — the directive (never blank) empty state, lifted 1:1
 * from `.gm-empty` in `components/admin/GalleryManager.astro`
 * (anton/forrest#1021), mandated across every admin surface by
 * `admin-shell#FR-7` ("directive, never blank, empty and error states").
 *
 * Renders UNCONDITIONALLY (with `hidden` when the surface currently has
 * content) rather than only when the collection is empty at SSR time — that
 * conditional-rendering was `anton/forrest#1021` DEFECT 3: GalleryManager's
 * `<ol id="gm-grid">` and `<p id="gm-empty">` were an either/or ternary, so
 * `#gm-empty` never existed in the DOM on a page that started non-empty, and
 * deleting the last photo left the operator staring at a bare, empty `<ol>`
 * instead of the directive copy. Always emitting `<park-admin-empty hidden>`
 * makes that unreachable-node bug structurally impossible for any future
 * consumer of this element.
 *
 * Author syntax:
 *   <park-admin-empty text="Galleriassa ei ole vielä kuvia."></park-admin-empty>
 *
 * Styling lives in wc/css/park-admin-empty.css — no <style> here, no shadow DOM.
 *
 * @dsSpec admin-shell#FR-7 — the directive empty state.
 *   (Traceability: specs/design-system/traceability.md; the generated card
 *   carries this same ref in its @dsCard marker — anton/forrest#846.)
 */

import { ParkElement, defineElement, esc } from "./base";

const TAG = "park-admin-empty";

export interface ParkAdminEmptyData {
	text?: string;
}

/** Pure render: text → inner HTML string. Safe server-side and client-side. */
export function renderParkAdminEmpty(data: ParkAdminEmptyData): string {
	return esc(data.text ?? "");
}

class ParkAdminEmpty extends ParkElement {
	static get observedAttributes(): string[] {
		return ["text"];
	}

	protected render(): string {
		return renderParkAdminEmpty({ text: this.attr("text") });
	}
}

defineElement(TAG, ParkAdminEmpty);

export { ParkAdminEmpty };
